import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, Transition } from "@headlessui/react";
import { toast } from "react-hot-toast";
import {
  ChevronDownIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import {
  isCalculationRequestFailed,
  saveOfferCalculationToTemplate,
} from "../../../../api/offer-calculation";
import {
  isOfferCalculationTemplateVersionsEnabled,
  resolveOfferActionErrorMessage,
} from "../utils";
import CalculationVersionHistoryDialog from "./calculation-version-history";

/**
 * Toolbar actions for an offer-specific calculation that has been persisted
 * (`id`). «Lagre som ny mal» is always available. «Oppdater mal» and
 * «Malversjoner» require a linked template (`original_calculation_id`).
 */
export default function CalculationTemplateActions({
  calculation,
  featureFlags = {},
  onSaved,
}) {
  const { t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
  const [changeNote, setChangeNote] = useState("");
  const [newTemplateTitle, setNewTemplateTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const calculationId = calculation?.id;
  const templateId = calculation?.original_calculation_id;
  const versionsEnabled = isOfferCalculationTemplateVersionsEnabled(featureFlags);

  if (!calculationId) return null;

  const closeUpdateDialog = () => {
    if (submitting) return;
    setUpdateDialogOpen(false);
    setChangeNote("");
  };

  const closeNewTemplateDialog = () => {
    if (submitting) return;
    setNewTemplateDialogOpen(false);
    setChangeNote("");
    setNewTemplateTitle("");
  };

  const handleUpdateTemplate = async () => {
    setSubmitting(true);
    try {
      const result = await saveOfferCalculationToTemplate(calculationId, {
        template_id: templateId,
        change_note: changeNote || undefined,
      });
      if (!result?.id || isCalculationRequestFailed(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result,
            "offers.calculation.template_actions.update_failed"
          )
        );
        return;
      }
      toast.success(t("offers.calculation.template_actions.update_success"));
      setUpdateDialogOpen(false);
      setChangeNote("");
      await onSaved?.(result);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNewTemplate = async () => {
    if (!newTemplateTitle.trim()) {
      toast.error(t("offers.calculation.template_actions.title_required"));
      return;
    }
    setSubmitting(true);
    try {
      const result = await saveOfferCalculationToTemplate(calculationId, {
        create_new_template: true,
        title: newTemplateTitle.trim(),
        change_note: changeNote || undefined,
      });
      if (!result?.id || isCalculationRequestFailed(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result,
            "offers.calculation.template_actions.save_new_failed"
          )
        );
        return;
      }
      toast.success(t("offers.calculation.template_actions.save_new_success"));
      setNewTemplateDialogOpen(false);
      setChangeNote("");
      setNewTemplateTitle("");
      await onSaved?.(result);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Menu as="div" className="relative">
        <Menu.Button className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          <DocumentDuplicateIcon className="h-4 w-4" aria-hidden="true" />
          {t("offers.calculation.template_actions.menu_label")}
          <ChevronDownIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-1 w-56 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {versionsEnabled && templateId ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(true)}
                    className={`flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm text-slate-700 ${
                      active ? "bg-slate-50" : ""
                    }`}
                  >
                    <ClockIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    {t("offers.calculation.template_actions.versions")}
                  </button>
                )}
              </Menu.Item>
            ) : null}
            {templateId ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => setUpdateDialogOpen(true)}
                    className={`flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm text-slate-700 ${
                      active ? "bg-slate-50" : ""
                    }`}
                  >
                    <ArrowUpTrayIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                    {t("offers.calculation.template_actions.update_template")}
                  </button>
                )}
              </Menu.Item>
            ) : null}
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => setNewTemplateDialogOpen(true)}
                  className={`flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm text-slate-700 ${
                    active ? "bg-slate-50" : ""
                  }`}
                >
                  <DocumentDuplicateIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  {t("offers.calculation.template_actions.save_new_template")}
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>

      {templateId ? (
        <CalculationVersionHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          calculationId={templateId}
          titleKey="offers.calculation.template_actions.versions_title"
          onRestored={onSaved}
        />
      ) : null}

      <PMDialog
        open={updateDialogOpen}
        onClose={closeUpdateDialog}
        title="offers.calculation.template_actions.update_template"
        size="sm"
        closeOnOutsideClick={!submitting}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("offers.calculation.template_actions.update_description")}
          </p>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              {t("offers.calculation.template_actions.change_note_label")}
            </label>
            <textarea
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
              placeholder={t("offers.calculation.template_actions.change_note_placeholder")}
            />
          </div>
          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeUpdateDialog}
              disabled={submitting}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleUpdateTemplate}
              disabled={submitting}
              className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? t("Loading...")
                : t("offers.calculation.template_actions.update_confirm")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={newTemplateDialogOpen}
        onClose={closeNewTemplateDialog}
        title="offers.calculation.template_actions.save_new_template"
        size="sm"
        closeOnOutsideClick={!submitting}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {t("offers.calculation.template_actions.save_new_description")}
          </p>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              {t("offers.calculation.template_actions.title_label")}
            </label>
            <input
              type="text"
              value={newTemplateTitle}
              onChange={(e) => setNewTemplateTitle(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
              placeholder={t("offers.calculation.template_actions.title_placeholder")}
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">
              {t("offers.calculation.template_actions.change_note_label")}
            </label>
            <textarea
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
              placeholder={t("offers.calculation.template_actions.change_note_placeholder")}
            />
          </div>
          <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closeNewTemplateDialog}
              disabled={submitting}
              className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveNewTemplate}
              disabled={submitting}
              className="inline-flex justify-center rounded-lg border border-transparent bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? t("Loading...")
                : t("offers.calculation.template_actions.save_new_confirm")}
            </button>
          </div>
        </div>
      </PMDialog>
    </>
  );
}
